import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, MailWarning } from "lucide-react";
import AuthShell from "@/components/marketplace/AuthShell";
import api from "@/lib/api";
import { toast } from "sonner";

export default function VerifyEmailPage() {
  const [sp] = useSearchParams();
  const token = sp.get("token");
  const [state, setState] = useState(token ? "verifying" : "waiting"); // waiting|verifying|success|error
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [resendState, setResendState] = useState("idle");

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        setState("success");
      } catch (err) {
        setState("error");
        setError(err?.response?.data?.error || "Invalid or expired verification link");
      }
    })();
  }, [token]);

  const resend = async (e) => {
    e.preventDefault();
    setResendState("loading");
    try {
      const { data } = await api.post("/auth/verify-email/request", { email });
      setResendState("done");
      toast.success("If that email is on file, a new verification link is on its way.");
      if (data?.dev_verification_link) {
        toast.info(`Dev link: ${data.dev_verification_link}`);
      }
    } catch (_) {
      setResendState("done");
      toast.success("If that email is on file, a new verification link is on its way.");
    }
  };

  return (
    <AuthShell
      title={state === "success" ? "Email verified." : "Verify your email."}
      subtitle={
        state === "success"
          ? "Your account is now fully active — welcome aboard."
          : "Click the link in your inbox, or enter your email below to receive a new one."
      }
    >
      {state === "verifying" && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-[#252A34]/80" data-testid="verify-loading">
          Verifying your email…
        </div>
      )}
      {state === "success" && (
        <div className="space-y-4">
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-6 flex items-start gap-3" data-testid="verify-success">
            <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
            <div>
              <div className="font-display font-semibold text-emerald-900">You're all set.</div>
              <div className="text-sm text-emerald-900/80 mt-1">Head back to the app and start exploring.</div>
            </div>
          </div>
          <Link to="/" className="inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-6 py-3 font-medium hover:bg-[#11485a] transition-colors" data-testid="verify-back-home">
            Continue to Gulf Coast Work
          </Link>
        </div>
      )}
      {state === "error" && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-6 flex items-start gap-3" data-testid="verify-error">
          <MailWarning className="h-6 w-6 text-red-600 shrink-0" />
          <div>
            <div className="font-display font-semibold text-red-900">We couldn't verify that link.</div>
            <div className="text-sm text-red-900/80 mt-1">{error}</div>
          </div>
        </div>
      )}
      {(state === "waiting" || state === "error") && (
        <form onSubmit={resend} className="space-y-3 mt-6" data-testid="verify-resend-form">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-[#071b22]">Your email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="verify-resend-email"
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-[#071b22] outline-none focus:border-[#165a71] focus:ring-2 focus:ring-[#165a71]/20"
            />
          </label>
          <button
            type="submit"
            disabled={resendState === "loading"}
            data-testid="verify-resend-submit"
            className="w-full rounded-full bg-[#071b22] text-white px-6 py-3 font-medium hover:bg-black transition-colors disabled:opacity-60"
          >
            {resendState === "loading" ? "Sending…" : "Send me a new verification link"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
